import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plus, Trash2, BarChart2 } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/apiClient';

interface SequenceSummary {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
}

export function SequencesPage() {
  const { t } = useTranslation();
  const [sequences, setSequences] = useState<SequenceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await apiClient.get<SequenceSummary[]>('/api/v1/sequences');
      setSequences(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      await apiClient.post('/api/v1/sequences', { name: name.trim(), description: description || null });
      setShowModal(false);
      setName('');
      setDescription('');
      await load();
      showToast(t('sequences.saved'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('common.delete_confirm') || 'Delete?')) return;
    await apiClient.delete(`/api/v1/sequences/${id}`);
    await load();
    showToast(t('sequences.deleted'));
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <Breadcrumbs crumbs={[{ label: t('sequences.title') }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('sequences.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('sequences.subtitle')}</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('sequences.new')}
        </Button>
      </div>

      {toast && (
        <div className="bg-green-900/40 text-green-200 px-4 py-2 rounded text-sm">{toast}</div>
      )}

      {loading ? (
        <p className="text-muted-foreground">{t('common.loading')}</p>
      ) : sequences.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('sequences.empty')}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-3 font-medium">{t('sequences.table.name')}</th>
                <th className="text-left p-3 font-medium">{t('sequences.table.active')}</th>
                <th className="text-left p-3 font-medium">{t('sequences.table.created')}</th>
                <th className="text-left p-3 font-medium">{t('sequences.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sequences.map((seq) => (
                <tr key={seq.id} className="border-t hover:bg-muted/20">
                  <td className="p-3">
                    <Link
                      to={`/admin/sequences/${seq.id}/edit`}
                      className="font-medium hover:underline"
                    >
                      {seq.name}
                    </Link>
                    {seq.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{seq.description}</p>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      seq.isActive ? 'bg-green-900/40 text-green-200' : 'bg-muted text-muted-foreground'
                    }`}>
                      {seq.isActive ? t('common.yes') : t('common.no')}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {new Date(seq.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-3 flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      asChild
                    >
                      <Link to={`/admin/sequences/${seq.id}/analytics`}>
                        <BarChart2 className="w-3.5 h-3.5" />
                      </Link>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(seq.id)}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background rounded-xl shadow-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">{t('sequences.new')}</h2>
            <div className="space-y-3">
              <label className="block text-sm font-medium">{t('sequences.table.name')}</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('sequences.table.name')}
              />
              <label className="block text-sm font-medium">{t('common.description')}</label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowModal(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleCreate} disabled={creating || !name.trim()}>
                {t('common.create')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
